import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import LanguageSelector from './LanguageSelector';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-black/80 backdrop-blur-md border-b border-white/10' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center justify-between">
          
          {/* Desktop Menu - Hidden on mobile */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-10 ml-auto">
            <button 
              onClick={() => scrollToSection('about')}
              className="text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase relative group"
            >
              {t('nav.about')}
              <div className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-300 group-hover:w-full"></div>
            </button>
            <button 
              onClick={() => scrollToSection('music')}
              className="text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase relative group"
            >
              {t('nav.music')}
              <div className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-300 group-hover:w-full"></div>
            </button>
            <button 
              onClick={() => scrollToSection('visuals')}
              className="text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase relative group"
            >
              {t('nav.visuals')}
              <div className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-300 group-hover:w-full"></div>
            </button>
            <button 
              onClick={() => scrollToSection('join')}
              className="text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase relative group"
            >
              {t('nav.join')}
              <div className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-300 group-hover:w-full"></div>
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase relative group"
            >
              {t('nav.contact')}
              <div className="absolute bottom-0 left-0 w-0 h-px bg-white transition-all duration-300 group-hover:w-full"></div>
            </button>
            
            {/* Desktop Language Selector */}
            <div className="ml-4 pl-4 border-l border-white/20">
              <LanguageSelector />
            </div>
          </div>

          {/* Mobile Menu Button & Language */}
          <div className="lg:hidden flex items-center space-x-4 ml-auto">
            <LanguageSelector />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-white p-2 hover:bg-white/10 rounded-md transition-colors"
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center">
                <span className={`bg-white block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${isMobileMenuOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'}`}></span>
                <span className={`bg-white block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm my-0.5 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`bg-white block transition-all duration-300 ease-out h-0.5 w-6 rounded-sm ${isMobileMenuOpen ? '-rotate-45 -translate-y-1' : 'translate-y-0.5'}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen 
            ? 'max-h-96 opacity-100 mt-4' 
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="py-4 space-y-4 bg-black/90 backdrop-blur-md rounded-lg border border-white/10 px-4">
            <button 
              onClick={() => scrollToSection('about')}
              className="block w-full text-left text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase py-2"
            >
              {t('nav.about')}
            </button>
            <button 
              onClick={() => scrollToSection('music')}
              className="block w-full text-left text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase py-2"
            >
              {t('nav.music')}
            </button>
            <button 
              onClick={() => scrollToSection('visuals')}
              className="block w-full text-left text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase py-2"
            >
              {t('nav.visuals')}
            </button>
            <button 
              onClick={() => scrollToSection('join')}
              className="block w-full text-left text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase py-2"
            >
              {t('nav.join')}
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left text-white/70 hover:text-white transition-all duration-300 text-sm font-light tracking-[0.15em] uppercase py-2"
            >
              {t('nav.contact')}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;